import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AuthGoBack } from '../AuthDialog/AuthGoBack';
import { GuestRoutes } from '../../routers/GuestRouter';
import { Button, Grid } from '@mui/material';
import { AuthDialog } from '../AuthDialog/AuthDialog';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CodeInput } from '../CodeInput/CodeInput';
import { AuthContext } from '../../contexts/AuthContext';
import { EAuthResponse, useAuth } from '../../hooks/useAuth';
import { NotificationContext } from '../../contexts/NotificationContext';
import { styled } from '@mui/styles';
import { TypographyButton, TypographySubtitle1 } from '../Typography/Typography';
import { useCountdown } from '../../hooks/useCountdown';
import { useEnumState } from '../../hooks/useEnumState';
import { useRedirectAfterAuth } from '../../hooks/useRedirectAfterAuth';
import { useHistoryWithSearch } from '../../hooks/useHistoryWithSearch';

const ErrorText = styled(TypographySubtitle1)(({ theme }) => ({
  color: theme.palette.error.main,
}));

const CountdownText = styled(TypographyButton)(({ theme }) => ({
  color: theme.palette.text.disabled,
}));

const RenewGridItem = styled(Grid)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'end',
});

const MIN_RENEW_INTERVAL = 60000;

const formatRemainingTime = (milliseconds: number) => {
  return `${Math.floor(milliseconds / 60000)}:${Math.floor((milliseconds % 60000) / 1000)
    .toString(10)
    .padStart(2, '0')}`;
};

const BoldCrucialData: FC<{ text: string }> = ({ text }) => {
  const { before, crucial, after, plain } = useMemo(() => {
    const emailPattern = /[^@\s]+@[^@\s]+/;
    const phoneNumberPattern = /\+? ?[0-9 -]{6,}/;
    const customNamePattern = /"[^"]+"/;

    const patterns = [emailPattern, phoneNumberPattern, customNamePattern];

    for (const pattern of patterns) {
      const partsPattern = RegExp(`^(.*) (${pattern.source}) (.*)$`);
      const partsMatches = text.match(partsPattern);
      if (partsMatches) {
        return {
          before: partsMatches[1] ? <BoldCrucialData text={partsMatches[1]} /> : null,
          crucial: <b>{partsMatches[2]}</b>,
          after: partsMatches[3] ? <BoldCrucialData text={partsMatches[3]} /> : null,
          plain: null,
        };
      }
    }
    return {
      before: null,
      crucial: null,
      after: null,
      plain: text,
    };
  }, [text]);

  return plain ? (
    <span>{plain}</span>
  ) : (
    <span>
      {before} {crucial} {after}
    </span>
  );
};

export const AuthSecondFactor: FC = () => {
  const { search } = useLocation();
  const { t } = useTranslation();
  const { auth2faCache, setAuth2faCache } = useContext(AuthContext);
  const { twoFactorAuth, renewMfa } = useAuth();
  const redirectAfterAuth = useRedirectAfterAuth();
  const { addNotification } = useContext(NotificationContext);
  const [error, setError] = useState(false);
  const [lastResendTimestamp, setLastResendTimestamp] = useState(Date.now());
  const [isHoveringRenew, onHoverRenew, onLeaveRenew] = useEnumState(false, true, false);
  const remainingUntilRenew = useCountdown(lastResendTimestamp + MIN_RENEW_INTERVAL, isHoveringRenew ? 1000 : null);
  const canResend = remainingUntilRenew === 0;
  const [code, setCode] = useState('');
  const { replace } = useHistoryWithSearch();

  useEffect(() => {
    setError(false);
  }, [code]);

  const mfaDevice = (auth2faCache?.devices ?? []).find((device) => device.is_default) || auth2faCache?.devices?.[0];

  const onSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (!mfaDevice) {
        return;
      }
      const result = await twoFactorAuth(mfaDevice.id, code);
      if (result === EAuthResponse.Success) {
        redirectAfterAuth();
      } else if (result === EAuthResponse.TokenExpired) {
        setAuth2faCache(null);
        addNotification({
          severity: 'error',
          message: t('auth.2fa.tokenExpired'),
        });
        replace(GuestRoutes.SignIn());
      } else if (result === EAuthResponse.Unexpected) {
        addNotification({
          severity: 'error',
          message: t('auth.2fa.unexpected'),
        });
      } else if (result === EAuthResponse.Unauthorized) {
        setError(true);
      }
    },
    [code, twoFactorAuth, redirectAfterAuth, mfaDevice]
  );

  const onRenewMfa = useCallback(async () => {
    if (!mfaDevice) {
      return;
    }
    const result = await renewMfa(mfaDevice.id);

    if (result === EAuthResponse.Unexpected) {
      addNotification({
        severity: 'error',
        message: t('auth.2fa.unexpected'),
      });
    } else if (result === EAuthResponse.Success) {
      setLastResendTimestamp(Date.now());
      addNotification({
        header: t('auth.2fa.reSend.notificationTitle'),
        severity: 'info',
        message: t('auth.2fa.reSend.notificationMessage'),
      });
    }
  }, [mfaDevice]);

  if (!auth2faCache || !mfaDevice) {
    return null;
  }

  return (
    <AuthDialog
      title={t('auth.email.encouragement')}
      subtitle={<BoldCrucialData text={auth2faCache.message} />}
      onSubmit={onSubmit}
      actions={[
        <AuthGoBack key="goBack" to={`${GuestRoutes.SignIn()}${search}`} />,
        <Button key="signIn" variant="contained" color="primary" type="submit" disabled={code.length < 6}>
          {t('auth.2fa.submitButton')}
        </Button>,
      ]}
    >
      <CodeInput pattern={mfaDevice.token_pattern} value={code} onChange={setCode} focusOnInit={true} error={error} />

      <Grid container>
        <Grid item xs={12} sm={7}>
          {error ? <ErrorText>{t('auth.2fa.invalid')}</ErrorText> : null}
        </Grid>
        {mfaDevice.can_resend ? (
          <RenewGridItem item xs={12} sm={5} textAlign="right" onMouseEnter={onHoverRenew} onMouseLeave={onLeaveRenew}>
            {isHoveringRenew && !canResend ? (
              <CountdownText>{formatRemainingTime(remainingUntilRenew)}</CountdownText>
            ) : null}
            <Button onClick={onRenewMfa} variant="text" color="secondary" size="small" disabled={!canResend}>
              {t('auth.2fa.reSend.button')}
            </Button>
          </RenewGridItem>
        ) : null}
      </Grid>
    </AuthDialog>
  );
};
